export interface LocationRole {
    userId: string;
    userName: string;
    userEmail: string;
    role: number;
}

// Mirrors the backend LocationStatusType (Swagger exposes it without names).
// Approval is per-location (Q14, 2026-06-14) — only Approved is checkout-eligible.
export enum LocationStatus {
    None = 0,
    Pending = 1,
    Approved = 2,
    Rejected = 3,
    Suspended = 4,
    // #434: passed review, but the client already pays and the location's
    // prorated first-month charge has not gone through, so it is NOT approved.
    // The customer's next successful card save (or an admin approving again)
    // completes the approval; an admin may also reject it.
    PendingPayment = 5,
}

// Mirrors the backend RejectionReasonCategoryType. Taxonomy for a per-location
// rejection (#197 / contract B). None is never a valid selection.
export enum RejectionReasonCategory {
    None = 0,
    InvalidCertificate = 1,
    InsufficientDocumentation = 2,
    DuplicateApplication = 3,
    TaxIdMismatch = 4,
    Other = 5,
}

export const REJECTION_REASON_CATEGORY_LABEL: Record<
    RejectionReasonCategory,
    string
> = {
    [RejectionReasonCategory.None]: "—",
    [RejectionReasonCategory.InvalidCertificate]: "Invalid certificate",
    [RejectionReasonCategory.InsufficientDocumentation]:
        "Insufficient documentation",
    [RejectionReasonCategory.DuplicateApplication]: "Duplicate application",
    [RejectionReasonCategory.TaxIdMismatch]: "Tax ID mismatch",
    [RejectionReasonCategory.Other]: "Other",
};

// The selectable categories, in menu order (None is not a valid rejection reason).
export const REJECTION_REASON_CATEGORY_OPTIONS: RejectionReasonCategory[] = [
    RejectionReasonCategory.InvalidCertificate,
    RejectionReasonCategory.InsufficientDocumentation,
    RejectionReasonCategory.DuplicateApplication,
    RejectionReasonCategory.TaxIdMismatch,
    RejectionReasonCategory.Other,
];

// Body of POST locations/{id}/reject (#197 / contract B). Category required,
// notes optional.
export interface RejectLocationRequest {
    rejectionReasonCategory: RejectionReasonCategory;
    rejectionReasonNotes?: string;
}

// Subscription types moved to @models/organization (#352 / #313): there is one
// subscription per ORGANIZATION, not per location.

export interface LocationResponse {
    id: string;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    isActive: boolean;
    // Optional so pre-status payloads and existing fixtures still type-check (#302).
    status?: LocationStatus;
    // Present when status is Rejected (#198) — why, so the owner can fix and
    // resubmit in place. Numeric on the wire like every response enum.
    rejectionReasonCategory?: RejectionReasonCategory;
    rejectionReasonNotes?: string;
    // #437: at least one resale certificate is on record — the same rule
    // approval uses to skip a location. Optional, like `status`, so fixtures
    // that predate it still type-check; the API always sends it.
    hasCertificate?: boolean;
    // #404: the API's ship-to answer for THIS caller, computed by its
    // ShipToLocationResolver, so the client keeps no copy of the rule.
    // isShipToEligible: a read may name this location. isDefaultShipTo: a read
    // that names none is answered for this one; false on every row for an admin
    // (whose reads are unfiltered) and for a caller with no eligible location.
    // Optional, like `status`, so fixtures that predate them still type-check;
    // the API always sends both, and a missing flag counts as false.
    isShipToEligible?: boolean;
    isDefaultShipTo?: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    roles: LocationRole[];
}

// One resale certificate on record (E12-S5 / #240). Downloadable by an admin
// at GET locations/{locationId}/files/{fileId}.
export interface LocationFile {
    id: string;
    fileName: string;
    uploadedAt: string;
}

// GET /locations/{id} — ADMIN-only (RequiredRoles [Admin]); the customer's own
// list is GET /locations. Shaped after the API's GetLocationResponse, which
// carries no status field. `files` arrive NEWEST FIRST — the first one is the
// re-uploaded certificate the pending-review badge points at.
export interface LocationDetailResponse {
    id: string;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    isActive: boolean;
    // Derived server-side: a certificate was uploaded after the admin last
    // reviewed one. Acknowledging clears it.
    hasPendingCertReview: boolean;
    files: LocationFile[];
    createdAt: string;
    updatedAt: string | null;
    createdBy: string;
    updatedBy: string | null;
    roles: LocationRole[];
}

export interface CreateLocationRequest {
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
}

export interface CreateLocationResponse {
    id: string;
}

export interface UpdateLocationRequest {
    name: string;
}

// PUT locations/{locationId}/address (C6, API #433): a platform admin edits a
// client location's address, as multipart. `updatedAt` is the client detail
// read's own string, sent back untouched (null: left out, which the API reads
// as "never changed"). `resaleCertificate` goes only with a change of state,
// which sends the location back to review; the API refuses it otherwise.
export interface UpdateLocationAddressRequest {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    updatedAt: string | null;
    resaleCertificate: File | null;
}

export interface UpdateLocationAddressResponse {
    updatedAt: string | null;
    status: LocationStatus;
    // A change of state moved the location back to review (Pending).
    sentBackToReview: boolean;
    certificateId: string | null;
}
